/* eslint-disable camelcase */
import { QueryFunctionContext, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { IData, IDataNoPagination } from '../types/shared';
import { onSocietarioGerado } from '../pages/societario/eventos';

// ── Enums ──

export type TipoOperacaoSocietario = 'constituicao' | 'alteracao' | 'transformacao';

export type TipoVinculoSocio = 'socio' | 'socio_administrador' | 'administrador';

export type TipoPessoaSocio = 'pf' | 'pj';

export type StatusContratoSocietario =
  // Pos-Fase 6: stub criado pelo upload-com-vigente, antes do wizard avancar.
  | 'rascunho_inicial'
  | 'rascunho'
  | 'campos_pendentes'
  // Pos-Fase 6 (gap dos validators): DOCX gerado, mas com criticos
  // NAO-overridable que bloqueiam transicao pra `gerado`.
  | 'em_revisao'
  | 'gerado'
  | 'assinado'
  | 'cancelado'
  // Sessao 2026-05-08 (Fase D): contrato fora do funnel mas mantido pra
  // consulta. Decisao organizacional do operador, nao terminal de erro.
  | 'arquivado';

// Fase 4 Slice E — estado civil e regime de casamento (Literal no backend).
export type EstadoCivil =
  | 'solteiro'
  | 'casado'
  | 'divorciado'
  | 'viuvo'
  | 'uniao_estavel'
  | 'separado_judicialmente';

export type RegimeCasamento =
  | 'comunhao_parcial_de_bens'
  | 'comunhao_universal_de_bens'
  | 'separacao_total_de_bens'
  | 'separacao_obrigatoria_de_bens'
  | 'participacao_final_nos_aquestos'
  | 'estrangeiro_nao_aplicavel';

// Fase 4 Slice A — tipos do request de /gerar.
export type TipoAlteracao =
  | 'quadro_societario'
  // Profissao, estado civil, nome ou endereco de quem continua, sem mexer no quadro (25/09).
  | 'dados_socio'
  | 'capital'
  | 'endereco'
  | 'razao_social'
  // Tipo PROPRIO desde 2026-08-13. Antes era sub-campo liberado por
  // `razao_social`, o que obrigava a marcar "Razão social" pra mexer so' na
  // fantasia — e fazia o front emitir um patch de razao social junto, que so'
  // nao virava clausula porque o back no-opa quando o valor e' igual ao
  // vigente. Separados, o patch fantasma deixa de existir.
  | 'nome_fantasia'
  | 'objeto_social'
  | 'administracao'
  // Alterar ou incluir cláusula do vigente pelo chat, e textos prontos (15/10).
  | 'clausulas'
  // 2026-08-27 (operador): a legalizacao precisa marcar "natureza juridica" no
  // card. E um dos SEIS eventos que exigem viabilidade no REDESIM, entao sem ele
  // o card derivava a rota errada (B em vez de A). Fica `oculto` no wizard (ver
  // TIPO_ALTERACAO_OPTIONS): nao existe gerador de documento pra troca de
  // natureza fora da transformacao EI/MEI -> LTDA, que ja tem operacao propria
  // (`tipo_operacao='transformacao'`). E marcador de PROCESSO, nao de geracao.
  | 'natureza_juridica'
  // Abertura de filial (Mylena, 21/09): cláusula no ato + parágrafo na sede.
  | 'abertura_filial'
  // Extinção de filial (08/10): cláusula no ato com endereço, NIRE e CNPJ da filial. Na
  // consolidação, o back retira o que só trata dela e a vistoria lista o que saiu (09/10).
  | 'extincao_filial'
  // Mudança de endereço de filial (09/10): cláusula no ato com o CNPJ da filial, o endereço de
  // hoje e o novo. Na consolidação o back troca o endereço e a vistoria avisa.
  | 'alteracao_filial'
  | 'outras';

export type EnquadramentoEmpresarial = 'me' | 'epp' | 'demais';

// Schema-extension v3 (elo 3, 2026-05-25) — gênero PF + porte da empresa.
// `Genero` alimenta concordância gramatical no contrato v3 (filtros `concordar`,
// `lista_administradores`, `qualificacao_corrida`).
export type Genero = 'M' | 'F';
// `PorteEmpresarial` é o porte v3 (EPP/ME/nenhum). Distinto do
// `EnquadramentoEmpresarial` (me/epp/demais) que é parâmetro do `/gerar` body
// pra v1/v2. v3 lê `porte` da coluna do contrato (M3).
export type PorteEmpresarial = 'EPP' | 'ME' | 'nenhum';

// Schema-extension v3 elo 3 — sub-objetos pra payload estruturado.
// Reusados em POST /contratos + PATCH /contratos/{id} + leitura via GET.
// Backend (`IEnderecoEstruturado` em contrato_societario.py) desempacota
// sub-objeto → 7 colunas flat via model_validator(mode='before').
export interface IEnderecoEstruturado {
  logradouro?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  uf?: string | null;
  cep?: string | null;
}

/**
 * Filial do ato, como vai em `filiais` do contrato (JSONB livre no back). Sem `acao` é filial
 * ABERTA (só o endereço); com `acao: 'fechar'` é filial ENCERRADA, e leva NIRE e CNPJ dela; com
 * `acao: 'alterar'` é filial que MUDA DE ENDEREÇO: os 7 campos são o endereço de hoje e
 * `endereco_novo` é para onde ela vai.
 */
export interface IFilialDoAto extends IEnderecoEstruturado {
  acao?: 'abrir' | 'fechar' | 'alterar' | null;
  nire?: string | null;
  cnpj?: string | null;
  endereco_novo?: IEnderecoEstruturado | null;
  // Facultativos, na abertura e na alteração (DREI, item 4.12.2).
  atividades?: string | null;
  capital_destacado?: number | null;
  nome_fantasia?: string | null;
}

// Representante legal da PJ sócia — ficha PF completa.
// Simetria com `DadosPessoaisPJ.representante: DadosPessoaisPF` do schema v3.
// `endereco_estruturado` aninhado é desempacotado recursivamente pelo backend
// pros 7 campos `representante_endereco_*` no banco.
export interface IRepresentantePJ {
  nome?: string | null;
  cpf?: string | null;
  rg?: string | null;
  rg_orgao?: string | null;
  rg_via?: string | null;
  nacionalidade?: string | null;
  estado_civil?: EstadoCivil | null;
  regime_casamento?: RegimeCasamento | null;
  dt_nascimento?: string | null;
  profissao?: string | null;
  genero?: Genero | null;
  endereco_estruturado?: IEnderecoEstruturado | null;
}

// Transformação EI→LTDA (Fase 2) — snapshot do Empresário Individual de origem.
// Persiste como JSONB `dados_ei`. Só o EI-EXCLUSIVO: denominação (≠ razão social
// da LTDA), qualificação do titular (PF) e DOIS endereços (residência + sede
// ORIGINAL do EI). cnpj/nire/data_constituicao/capital_social reusam as colunas
// do contrato (a LTDA herda por sub-rogação). dt_nascimento em ISO 'YYYY-MM-DD'.
export interface IDadosEmpresarioIndividual {
  denominacao?: string | null;
  nome?: string | null;
  cpf?: string | null;
  rg?: string | null;
  rg_orgao?: string | null;
  rg_via?: string | null;
  nacionalidade?: string | null;
  estado_civil?: EstadoCivil | null;
  regime_casamento?: RegimeCasamento | null;
  dt_nascimento?: string | null;
  naturalidade?: string | null;
  profissao?: string | null;
  genero?: Genero | null;
  // Endereço de RESIDÊNCIA do empresário.
  endereco_estruturado?: IEnderecoEstruturado | null;
  // Sede ORIGINAL do EI (distinta da sede nova da LTDA).
  sede_estruturada?: IEnderecoEstruturado | null;
}

// Consulta de CNPJ (BrasilAPI + CNPJA) pra autopreencher um sócio PJ.
// Backend: GET /api/v1/societario/cnpj/consultar (consultar_cnpj_societario).
// `sede` reusa IEnderecoEstruturado (mesmo shape do `endereco_estruturado` do sócio).
export interface ICnpjConsultaResponse {
  cnpj: string;
  razao_social?: string | null;
  nome_fantasia?: string | null;
  telefone?: string | null;
  email?: string | null;
  junta_uf?: string | null;
  nire?: string | null;
  sede: IEnderecoEstruturado;
  representante_nome?: string | null;
  // null quando a fonte mascara o CPF (bases públicas) — operador digita à mão.
  representante_cpf?: string | null;
  representante_cpf_mascarado?: string | null;
  fonte?: string | null;
}

// Fase 5 — issues dos validators v2.
export type IssueSeveridade = 'critico' | 'sugestao' | 'forma';
export type IssueCamada = 1 | 2 | 3 | 4 | 5;

// ── Interfaces ──

export interface ICnaeItem {
  codigo: string;
  descricao: string;
  principal?: boolean;
}

export interface ICnae {
  codigo: string;
  descricao: string;
  secao?: string | null;
  divisao?: string | null;
  grupo?: string | null;
  classe?: string | null;
}

export interface ISugerirCnaesResponse {
  principal: ICnae;
  secundarios: ICnae[];
  raciocinio: string;
}

export interface IModeloReferencia {
  id: number;
  nome: string;
  tipo_operacao: TipoOperacaoSocietario;
  versao: string;
  s3_key?: string | null;
  ativo: boolean;
  descricao?: string | null;
  corpo_referencia?: string | null;
  instrucoes_globais?: string | null;
  empresa_id?: number | null;
  responsavel_calibracao_id?: number | null;
  dt_ultima_revisao?: string | null;
  dt_criacao?: string;
  dt_atualizacao?: string;
}

export interface ICriarModeloRequest {
  tipo_operacao: TipoOperacaoSocietario;
  nome: string;
  corpo_referencia: string;
  instrucoes_globais?: string | null;
  descricao?: string | null;
  empresa_id?: number | null;
  responsavel_calibracao_id?: number | null;
}

export interface IContratoExemplo {
  id: number;
  contrato_id: number;
  nome: string;
  entidades_redacted: Record<string, number>;
  content_type?: string | null;
  tamanho_bytes?: number | null;
  dt_criacao: string;
}

export interface IUploadExemploResponse {
  exemplo: IContratoExemplo;
  entidades_redacted: Record<string, number>;
  aviso: string;
}

export interface IDadosContratoVigente {
  razao_social?: string | null;
  /** Bloco 13 (2026-06-03): nome fantasia / titulo do estabelecimento. */
  nome_fantasia?: string | null;
  cnpj?: string | null;
  nire?: string | null;
  capital_social_atual?: number | null;
  objeto_social_atual?: string | null;
  /**
   * Bloco 13 (2026-06-03): CNAEs estruturados extraidos da clausula do
   * objeto social. Vazio quando contrato so tem texto livre sem codigos
   * formatados — `objeto_social_atual` preserva a string nesse caso.
   */
  cnaes_atual?: Array<{ codigo: string; descricao: string }>;
  /**
   * Issue #1158: quando o vigente NÃO lista código CNAE (objeto em prosa — a
   * maioria dos contratos), o back sugere CNAEs a partir da prosa via IA
   * (escolhidos DENTRE o tb_cnae, nunca inventados). O operador confirma/ajusta
   * no Step Empresa antes de gerar. Vazio quando `cnaes_atual` já tem códigos.
   */
  cnaes_sugeridos?: Array<{ codigo: string; descricao: string; principal?: boolean }>;
  endereco_atual?: string | null;
  /** Bloco 13 (2026-06-03): cidade do foro eleito. */
  foro?: string | null;
  /** Bloco 13 (2026-06-03): cidade da assinatura (fechamento do contrato). */
  cidade_assinatura?: string | null;
  /** Bloco 13 (2026-06-03): nome completo do estado da Junta Comercial. */
  junta_comercial_estado?: string | null;
  /** Bloco 13 (2026-06-03): sigla da Junta Comercial (JUCEG, JUCESP, etc). */
  junta_comercial_sigla?: string | null;
  /** Bloco 13 (2026-06-03): data de inicio das atividades — ISO YYYY-MM-DD. */
  data_inicio_atividades?: string | null;
  numero_ultima_alteracao?: number | null;
  /**
   * Sessao 2026-05-11 (item 22 Fase 5): data de constituicao ORIGINAL da
   * sociedade (NAO da ultima alteracao). Extraida do preambulo/clausulas
   * finais do contrato vigente quando localizavel; null se a IA nao tiver
   * certeza absoluta. ISO YYYY-MM-DD.
   */
  data_constituicao?: string | null;
  quadro_socios_atual: Array<{
    nome: string;
    cpf?: string | null;
    cnpj?: string | null;
    quotas?: number | null;
    percentual?: number | null;
    /** Pos-Fase 6 (extractor estendido) — vinculo no contrato. */
    tipo_vinculo?: TipoVinculoSocio | null;
    // PF
    rg?: string | null;
    rg_orgao_emissor?: string | null;
    nacionalidade?: string | null;
    estado_civil?: EstadoCivil | null;
    regime_casamento?: RegimeCasamento | null;
    /** Cônjuge, quando a qualificação do vigente o cita (leitura desde 05/10). */
    nome_conjuge?: string | null;
    cpf_conjuge?: string | null;
    profissao?: string | null;
    dt_nascimento?: string | null;
    // PJ
    representante_nome?: string | null;
    representante_cpf?: string | null;
    // Contato (qualquer tipo)
    endereco?: string | null;
    email?: string | null;
    telefone?: string | null;
  }>;
}

export interface IContratoVigente {
  contrato_id: number;
  s3_key: string;
  presigned_url?: string | null;
  texto_extraido?: string | null;
  dados_extraidos?: IDadosContratoVigente | null;
  mensagem_extracao_ia?: string | null;
  /** Pos-Fase 6: codigo do erro IA quando extracao falhou. Null = sucesso. */
  extraction_error?: string | null;
}

/**
 * Resposta do POST /contratos/iniciar-alteracao-com-vigente (pos-Fase 6).
 * Cria contrato em `rascunho_inicial` + extracao IA do PDF/DOCX. Diferente
 * de `IContratoVigente` por nao retornar texto_extraido (frontend nao precisa).
 */
export interface IIniciarAlteracaoResponse {
  contrato_id: number;
  s3_key: string;
  presigned_url?: string | null;
  dados_extraidos?: IDadosContratoVigente | null;
  mensagem_extracao_ia?: string | null;
  extraction_error?: string | null;
}

/**
 * Sessao 2026-05-11 (socios sync) — acao explicita do operador em alteracao
 * contratual. Promove ao Nivel 1 a inferencia que a IA fazia por comparacao
 * com o vigente. Em constituicao e null (nao aplicavel).
 */
export type AcaoSocio = 'manter' | 'sair' | 'alterar' | 'adicionar';

/**
 * Sessao 2026-05-11 (item 15 Fase 4): sub-aspectos da alteracao quando
 * socio.acao=alterar. Espelha `TipoAlteracaoSocio` do backend.
 */
export type TipoAlteracaoSocio =
  | 'quotas'
  | 'endereco'
  | 'nome'
  | 'profissao'
  | 'vinculo'
  | 'estado_civil'
  | 'representante';

/**
 * Destino de redistribuicao quando socio sai (acao=sair). Multi-destino:
 * A sai, B pega 60%, C pega 40% — 2 entradas no array.
 */
export interface IDestinoQuota {
  socio_ordem: number;
  qtd: number;
}

export interface IContratoSocio {
  id?: number;
  contrato_id?: number;
  tipo_vinculo: TipoVinculoSocio;
  tipo_pessoa: TipoPessoaSocio;
  // SCP (Fase 3) — papel ostensiva/participante.
  papel_scp?: 'ostensiva' | 'participante' | null;
  ordem: number;
  quotas?: number | null;
  percentual?: number | string | null;
  nome: string;
  // PF
  cpf?: string | null;
  rg?: string | null;
  rg_orgao_emissor?: string | null;
  // Schema-extension v3 M2a — RG via (2a, 3a...). Renderizado pelo template
  // v3 quando preenchido (`qualificacao_corrida` PF).
  rg_via?: string | null;
  nacionalidade?: string | null;
  estado_civil?: EstadoCivil | string | null;
  // Fase 4 Slice E — regime de casamento + conjuge.
  regime_casamento?: RegimeCasamento | string | null;
  pacto_antenupcial?: boolean | null;
  nome_conjuge?: string | null;
  cpf_conjuge?: string | null;
  profissao?: string | null;
  dt_nascimento?: string | null;
  filiacao_pai?: string | null;
  filiacao_mae?: string | null;
  naturalidade?: string | null;
  // Schema-extension v3 M1 — gênero PF. Alimenta concordância no contrato v3.
  genero?: Genero | null;
  // PJ
  cnpj?: string | null;
  // Schema-extension v3 M2 — registro próprio da PJ sócia (distinto do
  // `tb_contrato_societario.junta_comercial_*` que é da empresa sendo
  // constituída).
  junta_uf?: string | null;
  nire?: string | null;
  // `representante_nome` + `representante_cpf` MANTIDOS visíveis na response
  // pra compat com front atual (StepRevisao.tsx). Outros 16 campos do
  // representante (M2/M2a) vivem dentro do sub-objeto `representante`
  // (computed_field na response) — backend mantém os flat ocultos via
  // `Field(exclude=True)`. Migração gradual: front vai ler `s.representante?.nome`
  // com fallback `?? s.representante_nome`; quando todos consumidores
  // migrarem, backend pode esconder também os 2 flat. Fallback tem data de
  // validade — não pode virar permanente (vide StepRevisao.tsx do elo 3).
  representante_nome?: string | null;
  representante_cpf?: string | null;
  // Sub-objeto `representante` — read only na response (computed_field do
  // backend). Pro POST/PATCH, cliente envia diretamente o sub-objeto e o
  // backend desempacota nos 18 campos flat via model_validator(mode='before').
  representante?: IRepresentantePJ | null;
  // Contato
  endereco?: string | null;
  email?: string | null;
  telefone?: string | null;
  // Schema-extension v3 M1 — endereço estruturado (residência PF / sede PJ).
  // Read only na response (computed_field). Pro write, cliente envia sub-objeto
  // ou flat — vide IContratoSocioCreate.
  endereco_estruturado?: IEnderecoEstruturado | null;
  // Sessao 2026-05-11 (socios sync)
  acao?: AcaoSocio | null;
  redistribuicao_quotas?: IDestinoQuota[] | null;
  // Sessao 2026-05-11 (item 15 Fase 4)
  tipos_alteracao_socio?: TipoAlteracaoSocio[] | null;
  // Porta 2 Wave 1 (2026-06-18): socio-admin que sai mas permanece admin nao-socio.
  permanece_como_administrador?: boolean | null;
  dt_criacao?: string;
  dt_atualizacao?: string;
}

export interface IContratoSocioCreate {
  tipo_vinculo: TipoVinculoSocio;
  tipo_pessoa: TipoPessoaSocio;
  // SCP (Fase 3) — papel ostensiva/participante.
  papel_scp?: 'ostensiva' | 'participante' | null;
  ordem?: number;
  quotas?: number | null;
  percentual?: number | string | null;
  nome: string;
  cpf?: string | null;
  rg?: string | null;
  rg_orgao_emissor?: string | null;
  rg_via?: string | null;
  nacionalidade?: string | null;
  estado_civil?: EstadoCivil | null;
  // Fase 4 Slice E — backend valida via Literal: regime obrigatorio se
  // estado_civil em {casado, uniao_estavel}. Frontend tambem checa em 6-B.
  regime_casamento?: RegimeCasamento | null;
  pacto_antenupcial?: boolean | null;
  nome_conjuge?: string | null;
  cpf_conjuge?: string | null;
  profissao?: string | null;
  dt_nascimento?: string | null;
  filiacao_pai?: string | null;
  filiacao_mae?: string | null;
  naturalidade?: string | null;
  // Schema-extension v3 M1 — gênero PF.
  genero?: Genero | null;
  cnpj?: string | null;
  // Schema-extension v3 M2 — PJ sócia (registro próprio).
  junta_uf?: string | null;
  nire?: string | null;
  representante_nome?: string | null;
  representante_cpf?: string | null;
  endereco?: string | null;
  email?: string | null;
  telefone?: string | null;
  // Schema-extension v3 elo 3 — sub-objetos ergonômicos pra POST/PATCH.
  // Backend desempacota via model_validator(mode='before') → 7 endereco_*
  // + 18 representante_* no banco. Ambiguidade (sub-objeto + flat juntos)
  // rejeita com PydanticCustomError no backend.
  endereco_estruturado?: IEnderecoEstruturado | null;
  representante?: IRepresentantePJ | null;
  // Sessao 2026-05-11 (socios sync)
  acao?: AcaoSocio | null;
  redistribuicao_quotas?: IDestinoQuota[] | null;
  // Sessao 2026-05-11 (item 15 Fase 4)
  tipos_alteracao_socio?: TipoAlteracaoSocio[] | null;
  // Porta 2 Wave 1 (2026-06-18): socio-admin que sai mas permanece admin nao-socio.
  permanece_como_administrador?: boolean | null;
}

export type IContratoSocioUpdate = Partial<IContratoSocioCreate>;

export interface IContratoSocietario {
  id: number;
  empresa_id?: number | null;
  tipo_operacao: TipoOperacaoSocietario;
  contrato_pai_id?: number | null;
  template_id?: number | null;
  status: StatusContratoSocietario;
  razao_social?: string | null;
  nome_fantasia?: string | null;
  cnpj?: string | null;
  capital_social?: number | string | null;
  capital_social_extenso?: string | null;
  objeto_social?: string | null;
  cnaes?: ICnaeItem[] | null;
  endereco?: string | null;
  foro?: string | null;
  finalidade_livre?: string | null;
  // Campos novos (F2)
  data_inicio_atividades?: string | null;
  cidade_assinatura?: string | null;
  junta_comercial_estado?: string | null;
  junta_comercial_sigla?: string | null;
  nire?: string | null;
  data_constituicao?: string | null;
  numero_alteracao?: number | null;
  razao_social_consolidada?: string | null;
  // Saida IA
  variaveis_preenchidas?: Record<string, any> | null;
  campos_pendentes?: ICampoPendente[] | null;
  decisoes_ia?: Array<{ acao: string; alvo: string; motivo?: string }> | null;
  instrucoes_ia_historico?: Array<Record<string, any>> | null;
  contrato_s3_key?: string | null;
  contrato_hash?: string | null;
  // 2026-08-27 — DOCX reenviado pelo operador depois de ajustar a mão no
  // Word. Quando existe, é ELE o documento do ato; o gerado continua
  // acessível via `?original=true`.
  contrato_editado_s3_key?: string | null;
  contrato_editado_em?: string | null;
  contrato_editado_por?: number | null;
  /** Nome de quem enviou a revisão — computed_field do backend. */
  contrato_editado_por_nome?: string | null;
  /** Nome de quem abriu o contrato — computed_field do backend. */
  criado_por_nome?: string | null;
  /** Nome de quem confirmou a vistoria — computed_field do backend. */
  auditoria_revisada_por_nome?: string | null;
  // Fase 2.5 — contrato vigente em alteracoes.
  contrato_vigente_s3_key?: string | null;
  contrato_vigente_texto_extraido?: string | null;
  contrato_vigente_dados_extraidos?: IDadosContratoVigente | null;
  // Fase 3 — generator v2 (engine, snapshot do modelo, estrutura IA).
  // Schema-extension v3 elo 5: 'v3' adicionado. Coluna grava "v3" puro
  // (refinamento uni/pluri é interno do `_decidir_engine`, vem de
  // len(socios) — não vai pra coluna).
  engine_usado?: 'v1' | 'v2' | 'v3' | null;
  // Fase 1 auditor-gate (2026-07-03): revisão da vistoria de fidelidade. NÃO-
  // NULL ⟺ docx atual conscientemente revisado → libera o download (alteração
  // v3, bloqueio duro no back). "Pendente de revisão" = alteração + engine v3 +
  // docx + auditoria_revisada_em null. Só a alteração gera vistoria → só ela gateia.
  auditoria_revisada_em?: string | null;
  auditoria_revisada_id?: number | null;
  auditoria_revisada_por?: number | null;
  // Transformação EI→LTDA — snapshot do EI (JSONB); o backend retorna no GET.
  // Sem isto, a hidratação dependia de `(c as any).dados_ei` (review #517).
  dados_ei?: IDadosEmpresarioIndividual | null;
  // SCP (Fase 3) — config da natureza scp (JSONB passthrough).
  dados_scp?: Record<string, unknown> | null;
  task_id_em_andamento?: string | null;
  modelo_referencia_id?: number | null;
  modelo_referencia_versao?: string | null;
  estrutura_ia?: IAContractStructure | null;
  // Fase 4 Slice A — tipos marcados pelo operador no /gerar.
  tipos_alteracao?: TipoAlteracao[] | null;
  filiais?: IFilialDoAto[] | null;
  // Chat da administração (29/09): só leitura aqui; as rotas `/administracao/*` escrevem.
  administracao_ia?: IAdministracaoIA | null;
  // Chat "Outras cláusulas" (15/10): idem, rotas `/clausulas/*`.
  clausulas_ia?: IAdministracaoIA | null;
  /** Textos prontos marcados na constituição (chaves do back). */
  textos_prontos?: string[] | null;
  // Schema-extension v3 M3 — empresa estruturada. Standalone (visíveis na
  // response). Endereço via `endereco_estruturado` computed.
  foro_cidade?: string | null;
  foro_uf?: string | null;
  capital_qtd_quotas?: number | null;
  capital_valor_quota?: number | string | null;
  porte?: PorteEmpresarial | null;
  natureza_societaria?: 'ltda' | 'holding' | 'scp' | null;
  data_assinatura?: string | null;
  endereco_estruturado?: IEnderecoEstruturado | null;
  // Pos-Fase 6 (gap dos validators): resumo do validate_contrato espelhado
  // do log v2. Frontend usa pra render direto sem JOIN.
  issues_resumo?: IIssuesResumo | null;
  criado_por?: number | null;
  dt_criacao?: string;
  dt_atualizacao?: string;
  socios: IContratoSocio[];
}

export interface IContratoSocietarioCreate {
  empresa_id?: number | null;
  tipo_operacao: TipoOperacaoSocietario;
  contrato_pai_id?: number | null;
  template_id?: number | null;
  razao_social?: string | null;
  nome_fantasia?: string | null;
  cnpj?: string | null;
  capital_social?: number | string | null;
  capital_social_extenso?: string | null;
  objeto_social?: string | null;
  cnaes?: ICnaeItem[] | null;
  endereco?: string | null;
  foro?: string | null;
  finalidade_livre?: string | null;
  data_inicio_atividades?: string | null;
  cidade_assinatura?: string | null;
  junta_comercial_estado?: string | null;
  junta_comercial_sigla?: string | null;
  nire?: string | null;
  data_constituicao?: string | null;
  numero_alteracao?: number | null;
  razao_social_consolidada?: string | null;
  // Schema-extension v3 M3 — empresa estruturada (POST).
  foro_cidade?: string | null;
  foro_uf?: string | null;
  capital_qtd_quotas?: number | null;
  capital_valor_quota?: number | string | null;
  porte?: PorteEmpresarial | null;
  natureza_societaria?: 'ltda' | 'holding' | 'scp' | null;
  /** Constituição: textos prontos marcados (15/10). */
  textos_prontos?: string[] | null;
  data_assinatura?: string | null;
  // Sub-objeto ergonômico — backend desempacota nos 7 endereco_* da empresa.
  endereco_estruturado?: IEnderecoEstruturado | null;
  // Transformação EI→LTDA — snapshot do EI de origem (JSONB). Null nas demais ops.
  dados_ei?: IDadosEmpresarioIndividual | null;
  // SCP (Fase 3) — config da natureza scp (JSONB passthrough).
  dados_scp?: Record<string, unknown> | null;
  socios?: IContratoSocioCreate[];
  filiais?: IFilialDoAto[] | null;
}

export interface IContratoSocietarioUpdate {
  empresa_id?: number | null;
  template_id?: number | null;
  status?: StatusContratoSocietario;
  /**
   * Pos-Fase 6: flag explicita pra transicionar `rascunho_inicial → rascunho`
   * quando o usuario avanca do step `contrato_vigente` no wizard. Backend
   * valida que o status atual e `rascunho_inicial` (HTTP 409 se nao).
   */
  advance_from_initial?: boolean;
  razao_social?: string | null;
  nome_fantasia?: string | null;
  cnpj?: string | null;
  capital_social?: number | string | null;
  capital_social_extenso?: string | null;
  objeto_social?: string | null;
  cnaes?: ICnaeItem[] | null;
  endereco?: string | null;
  foro?: string | null;
  finalidade_livre?: string | null;
  data_inicio_atividades?: string | null;
  cidade_assinatura?: string | null;
  junta_comercial_estado?: string | null;
  junta_comercial_sigla?: string | null;
  nire?: string | null;
  data_constituicao?: string | null;
  numero_alteracao?: number | null;
  razao_social_consolidada?: string | null;
  variaveis_preenchidas?: Record<string, any> | null;
  /**
   * Sessao 2026-05-08 (contrato 68): sync REPLACE-ALL de socios no PATCH
   * antes de gerar. Sem isso, `tb_contrato_socio` ficava vazio (frontend
   * pulava o sync) e schema-force admin/validators que dependem de
   * `tipo_vinculo` nao tinham fonte de verdade. null = nao tocar nos socios.
   */
  socios?: IContratoSocioCreate[] | null;
  /**
   * Sessao 2026-05-08 (contrato 73): persiste tipos de alteracao no PATCH
   * pra que retomada de rascunho mantenha a selecao feita no wizard.
   * Antes ficava so no React Hook Form e era enviado apenas no /gerar.
   */
  tipos_alteracao?: TipoAlteracao[] | null;
  filiais?: IFilialDoAto[] | null;
  // Schema-extension v3 M3 — empresa estruturada (PATCH).
  foro_cidade?: string | null;
  foro_uf?: string | null;
  capital_qtd_quotas?: number | null;
  capital_valor_quota?: number | string | null;
  porte?: PorteEmpresarial | null;
  natureza_societaria?: 'ltda' | 'holding' | 'scp' | null;
  /** Constituição: textos prontos marcados (15/10). */
  textos_prontos?: string[] | null;
  data_assinatura?: string | null;
  endereco_estruturado?: IEnderecoEstruturado | null;
  dados_ei?: IDadosEmpresarioIndividual | null;
  // SCP (Fase 3) — config da natureza scp (JSONB passthrough).
  dados_scp?: Record<string, unknown> | null;
}

export interface ITemplateSocietario {
  id: number;
  nome: string;
  tipo_operacao: TipoOperacaoSocietario;
  versao: string;
  s3_key: string;
  ativo: boolean;
  descricao?: string | null;
  dt_criacao?: string;
  dt_atualizacao?: string;
}

export interface ITemplateSocietarioCreateForm {
  nome: string;
  tipo_operacao: TipoOperacaoSocietario;
  versao: string;
  descricao?: string | null;
  ativo?: boolean;
  file: File;
}

export interface ITemplateSocietarioUpdate {
  nome?: string;
  versao?: string;
  ativo?: boolean;
  descricao?: string | null;
}

export interface ICnhCampos {
  nome?: string | null;
  cpf?: string | null;
  rg?: string | null;
  dt_nascimento?: string | null;
  filiacao_pai?: string | null;
  filiacao_mae?: string | null;
  orgao_emissor?: string | null;
  nacionalidade?: string | null;
  endereco?: string | null;
}

export interface ICnhAnalyzeResponse {
  upload_id: number;
  s3_key: string;
  campos: ICnhCampos;
  textract_raw?: Record<string, any> | null;
}

// Documento de identidade generico (CNH+RG, PDF+JPG+PNG) — novo extrator
// refator de 2026-05-19. Endpoint POST /api/v1/societario/documento/analyze.
export type TipoDocumento = 'cnh' | 'rg' | 'outro';
export type TipoDocumentoRequest = 'cnh' | 'rg' | 'auto';

export interface IDocumentoCampos {
  tipo_documento?: TipoDocumento | null;
  // Comuns
  nome?: string | null;
  cpf?: string | null;
  rg?: string | null;
  orgao_emissor?: string | null;
  dt_nascimento?: string | null;
  filiacao_pai?: string | null;
  filiacao_mae?: string | null;
  nacionalidade?: string | null;
  endereco?: string | null;
  // Especificos CNH
  registro_cnh?: string | null;
  categoria?: string | null;
  validade?: string | null;
  primeira_habilitacao?: string | null;
  // Especificos RG
  naturalidade_cidade?: string | null;
  naturalidade_uf?: string | null;
  dt_expedicao?: string | null;
}

export interface IDocumentoAnalyzeResponse {
  upload_id: number;
  s3_key: string;
  tipo_documento?: TipoDocumento | null;
  campos: IDocumentoCampos;
  extracao_raw?: Record<string, any> | null;
}

export interface ICampoPendente {
  id: string;
  label?: string;
  descricao?: string;
  contexto?: string;
  paragrafo?: string;
  clausula?: string;
  tipo?: string;
  [key: string]: any;
}

export interface IDecisaoIa {
  acao: string;
  alvo: string;
  motivo?: string;
}

// Bloco 13 v3 alteração — discriminated union de patches estruturados.
// Espelha `app/schemas/contrato_societario.py:1022-1095` no back.
// engine_routing rejeita os não cobertos em D+E (`inserir_clausula`,
// `remover_clausula`, `ia_narrow`) com 422 — mantidos no union pra
// type-checkar a payload, mas operador só consegue marcar D+E na UI.
export type CampoSubstituivel =
  | 'razao_social'
  | 'nome_fantasia'
  | 'endereco'
  | 'capital_social'
  | 'objeto_social'
  | 'filial'
  | 'filial_fechar'
  | 'filial_alterar';

export interface IPatchSubstituirCampo {
  tipo: 'substituir_campo';
  clausula_numero: number;
  campo: CampoSubstituivel;
  valor_novo: any;
}

export interface IPatchSubstituirClausula {
  tipo: 'substituir_clausula';
  clausula_numero: number;
  novo_corpo: Record<string, any>;
}

export interface IPatchInserirClausula {
  tipo: 'inserir_clausula';
  apos_clausula_numero: number;
  titulo: string;
  corpo: Record<string, any>;
}

export interface IPatchRemoverClausula {
  tipo: 'remover_clausula';
  clausula_numero: number;
}

export interface IPatchIaNarrow {
  tipo: 'ia_narrow';
  clausula_numero?: number | null;
  contexto?: Record<string, any>;
}

export type IPatchClausula =
  | IPatchSubstituirCampo
  | IPatchSubstituirClausula
  | IPatchInserirClausula
  | IPatchRemoverClausula
  | IPatchIaNarrow;

/**
 * Item do `quadro_vigente_override` no /gerar v3 alteração.
 *
 * Front envia quando conhece o quadro de sócios atual (via extração IA
 * do upload do vigente) E sabe que o `extrair_quadro_vigente` regex do
 * back pode falhar (caso HUNTER CLUB: nome+CPF do sócio só no preâmbulo,
 * não na cláusula do capital). Sem override, validators de quadro
 * acusam falso positivo (V_QUOTAS_TOTAL_BATE_VIGENTE_V3).
 *
 * Usar quando `tipos_alteracao` NÃO inclui `quadro_societario` — o
 * quadro pós-alteração é o mesmo que o vigente.
 */
export interface ISocioVigenteOverride {
  nome: string;
  cpf?: string | null;
  cnpj?: string | null;
  quotas: number;
  /**
   * Papel no quadro ('socio' | 'socio_administrador' | 'administrador'). O back
   * distingue admin não-sócio de sócio por aqui (mais robusto que quotas, que a
   * extração pode zerar) ao montar o preâmbulo de declarantes. Review #965.
   */
  tipo_vinculo?: string | null;
}

export interface IGerarContratoRequest {
  instrucoes_adicionais?: string | null;
  /** Default 'demais'. Backend usa pra incluir/omitir clausula de enquadramento. */
  enquadramento_empresarial?: EnquadramentoEmpresarial;
  /** Obrigatorio em alteracao (>=1 item). Endpoint cruza com tipo_operacao do contrato. */
  tipos_alteracao?: TipoAlteracao[] | null;
  /**
   * Em alteracao: True forca consolidacao (re-emite todas as clausulas);
   * False forca alteracao simples (so ratificacao); null deixa IA decidir.
   * Resolve bug do plano onde IA anunciava consolidacao mas nao consolidava.
   */
  consolidar?: boolean | null;
  /**
   * Bloco 13 v3 alteração (D+E) — patches estruturados contra o vigente
   * parseado. Obrigatório quando engine='v3' + tipo_operacao='alteracao'.
   * engine_routing rejeita patches[] vazio em v3.
   */
  patches?: IPatchClausula[] | null;
  /**
   * Hash SHA1[:12] do vigente parsed no momento de montar os patches.
   * Strict ativo (A.39): null bypass desabilitado em prod. Endpoint
   * compara com hash atual; divergência → 412 VIGENTE_HASH_MISMATCH.
   */
  vigente_hash?: string | null;
  /**
   * Override do quadro vigente — usado quando o operador conhece o
   * quadro de sócios atual e o `extrair_quadro_vigente` regex-based do
   * back pode falhar pra vigentes com layout atípico.
   */
  quadro_vigente_override?: ISocioVigenteOverride[] | null;
}

/**
 * Response do `GET /parsear-vigente-preview` — wizard chama ao abrir
 * passo de alteração pra capturar `vigente_hash` (lock G-5) + exibir
 * cláusulas parseadas.
 */
/** Cláusula redigida no chat da administração. Parte B: `substitui` = nº no vigente (0 = nova). */
export interface IClausulaDoRascunho {
  titulo: string;
  texto: string;
  substitui: number;
  /** Parte B, cláusula nova: nº da cláusula do vigente depois da qual ela entra (0 = no fim). */
  depois_de?: number;
}

export interface IRascunhoDaAdministracao {
  parte_a: IClausulaDoRascunho[];
  parte_b: IClausulaDoRascunho[];
  /** Nºs das cláusulas do vigente que saem da consolidação. */
  excluidas?: number[];
  o_que_mudou: string;
  /** Outras cláusulas: textos prontos do ato (o back escreve o texto; a IA não o vê). */
  textos_prontos?: string[];
}

/** Assunto do chat da alteração: a mesma tela e as mesmas rotas. */
export type AssuntoDoChat = 'administracao' | 'clausulas';

/** Texto pronto como entra neste contrato (`GET /clausulas/textos-prontos`). */
export interface ITextoPronto {
  chave: string;
  rotulo: string;
  onde: string;
  parte_a: { titulo: string; texto: string } | null;
  parte_b: { titulo: string; texto: string };
}

export interface IMensagemDaAdministracao {
  papel: 'operador' | 'ia';
  texto: string;
  falta?: string[];
  pronto?: boolean;
  em?: string;
}

export interface IResumoDaAdministracao {
  resumo: string;
  /** Um fato por item, com a cláusula (desde 29/09; resumo mais antigo não tem). */
  pontos?: string[];
  administradores: { nome: string; socio: boolean; como_assina: string }[];
  /** `texto`: a cláusula do vigente na íntegra (outras cláusulas, 15/10; o resumo da IA não traz). */
  clausulas: { numero: number; do_que_trata: string; texto?: string }[];
}

/** Estado do chat gravado no contrato (`administracao_ia`). */
export interface IAdministracaoIA {
  vigente?: string;
  resumo?: IResumoDaAdministracao | null;
  historico: IMensagemDaAdministracao[];
  rascunho?: IRascunhoDaAdministracao | null;
  aprovado?:
    | (IRascunhoDaAdministracao & {
        em?: string;
        por?: number | null;
        /** Quem administrava no passo Sócios na hora de aprovar (desde 29/09). */
        pessoas?: IPessoaDaAdministracao[];
      })
    | null;
  custo_usd?: number;
  /** Pedido à IA rodando em segundo plano (a tela acompanha pelo GET). */
  pendente?: {
    resumo?: { task_id: string; desde?: string };
    mensagem?: { task_id: string; desde?: string; mensagem: string };
  };
  /** Último pedido que falhou: o texto do operador volta para a caixa. */
  erro?: { tipo: 'resumo' | 'mensagem'; detalhe: string; mensagem?: string; em?: string } | null;
  /** Sobe a cada gravação: resposta mais velha que a da tela é descartada. */
  versao?: number;
  /** Id da conversa (novo a cada recomeço): versão de outra conversa não se compara. */
  conversa?: string;
  /** Quem administrava no passo Sócios quando a IA redigiu o rascunho atual. */
  pessoas_do_rascunho?: IPessoaDaAdministracao[];
}

/** Quem aparece no chat: dado do formulário, nunca da IA. */
export interface IPessoaDaAdministracao {
  nome: string;
  /** Na sociedade: `continua`, `entra` ou `sai`. */
  situacao: 'continua' | 'entra' | 'sai';
  /** Sócio depois do ato (administrador não sócio: false). */
  socio: boolean;
  administra_hoje: boolean;
  administra_depois: boolean;
  qualificacao: string;
}

export interface IParsearVigentePreviewResponse {
  contrato_id: number;
  vigente_hash: string;
  parser_version: string;
  reparse_executado: boolean;
  parser_json: Record<string, any>;
}

/**
 * Resumo de validators rodados pelo generator v2. Espelhado em
 * `tb_contrato_societario.issues_resumo` desde o gap-fix pos-Fase 6 — antes
 * ficava so em `tb_societario_v2_geracao_log`. Frontend usa pra renderizar
 * counts + decidir UX (warning destacado quando `criticos_bloqueantes>0`).
 */
export interface IIssuesResumo {
  issues_total: number;
  criticos: number;
  /** Pos-Fase 6: criticos NAO-overridable. Bloqueia transicao pra `gerado`. */
  criticos_bloqueantes?: number;
  sugestoes: number;
  forma: number;
  // Campos contextuais opcionais.
  clausulas?: number;
  campos_pendentes?: number;
  validacao_falhou?: string;
}

export interface IGerarContratoResultado {
  // Pos-Fase 6 (gap dos validators): `em_revisao` quando ha criticos
  // NAO-overridable; DOCX gerado, mas contrato bloqueado pra avancar.
  status: 'gerado' | 'em_revisao' | 'campos_pendentes' | 'processing';
  campos_pendentes?: ICampoPendente[];
  variaveis_preenchidas?: Record<string, any>;
  contrato_s3_key?: string;
  contrato_rascunho_s3_key?: string;
  decisoes_ia?: IDecisaoIa[];
  issues_resumo?: IIssuesResumo;
  // A geracao assincrona devolve task_id (background task) e o custo estimado.
  task_id?: string;
  engine?: 'v3-unipessoal' | 'v3-pluripessoal' | 'v3-alteracao-consolidacao';
  cost_estimate_usd?: number;
  tokens_usados?: { input: number; output: number };
  [key: string]: any;
}

// Fase 5 — issues dos validators.
export interface IIssue {
  codigo: string;
  severidade: IssueSeveridade;
  camada: IssueCamada;
  overridable: boolean;
  campo: string;
  mensagem: string;
  valor_atual?: string | null;
  valor_proposto?: string | null;
}

// Fase 3 — estrutura JSON do tool_use (resumida — frontend so le).
export interface IClausulaIA {
  numero_ordinal: string;
  titulo: string;
  caput: string;
  paragrafos: string[];
}

export interface IAContractStructure {
  preambulo: string;
  qualificacao_socios: string[];
  considerandos?: string | null;
  clausulas: IClausulaIA[];
  ementa?: Array<{ numeracao_romana: string; titulo: string }>;
  fechamento: string;
  decisoes_ia?: IDecisaoIa[];
}

// Fase 5.5 — dashboard de metricas.
export interface IMetricaDia {
  data: string; // ISO date
  contratos_gerados: number;
  custo_total_usd: number;
  custo_medio_usd: number;
  latencia_p50_s: number;
  latencia_p95_s: number;
  taxa_issues_criticos: number;
  taxa_regeneracao: number;
}

export interface IAlertaMetrica {
  data: string;
  tipo: 'custo_acima_threshold' | 'taxa_critico_alta' | string;
  valor: number;
  threshold: number;
  mensagem: string;
}

export interface IMetricasResponse {
  time_series: IMetricaDia[];
  agregados: {
    dias: number;
    contratos_total: number;
    custo_total_usd: number;
    custo_medio_periodo_usd: number;
    latencia_p95_max_s: number;
  };
  alertas: IAlertaMetrica[];
}

export interface IStatusGeracaoV2 {
  status: 'processing' | 'completed' | 'failed' | 'no_task' | 'not_found';
  result?: IGerarContratoResultado;
  error?: string;
  engine?: string | null;
}

export interface IFinalizarContratoResultado {
  status: 'gerado' | 'campos_pendentes';
  campos_pendentes?: ICampoPendente[];
  contrato_s3_key?: string;
  contrato_hash?: string;
  [key: string]: any;
}

export interface IDownloadContratoResponse {
  url: string;
  s3_key: string;
  // `hash` descreve o DOCX GERADO. Vem null quando a URL aponta pra
  // versão revisada — comparar contra o arquivo errado seria pior que
  // não ter hash.
  hash?: string | null;
  versao?: 'gerada' | 'revisada';
  tem_versao_revisada?: boolean;
  editado_em?: string | null;
}

export interface IContratosFilters {
  page?: number;
  size?: number;
  empresa_id?: number;
  tipo_operacao?: TipoOperacaoSocietario;
  /** Um status ou varios separados por virgula ("gerado,assinado"). */
  status?: StatusContratoSocietario | string;
  criado_por?: number;
  incluir_arquivados?: boolean;
  /** Razao social, fantasia, CNPJ, numero (#123) ou nome de socio. */
  search?: string;
  /** Sem valor, o back devolve por id crescente. */
  ordenar?: 'atualizados' | 'recentes' | 'antigos';
}

/** Total de cada aba e os indicadores do cabecalho (`GET /contratos/contagem`). */
export interface IContagemContratos {
  todos: number;
  minhas: number;
  finalizados: number;
  arquivados: number;
  /** Rascunho, campos pendentes ou editado depois de gerar. */
  em_andamento: number;
  /** Alteracao v3 gerada com a vistoria por revisar (o download espera por ela). */
  vistoria_pendente: number;
}

export interface IContagemContratosFilters {
  empresa_id?: number;
  tipo_operacao?: TipoOperacaoSocietario;
  search?: string;
  criado_por?: number;
}

/** Quem abriu contratos ativos (`GET /contratos/criadores`): o filtro "Criado por". */
export interface ICriadorDeContratos {
  id: number;
  nome: string;
  total: number;
}

export interface ITemplatesFilters {
  page?: number;
  size?: number;
  tipo_operacao?: TipoOperacaoSocietario;
  ativo?: boolean;
}

// ── Socios Unicos (limpeza pos-Fase 6) ──
//
// Agregacao distinct cross-contratos. Backend usa window function partition
// por chave (cpf/cnpj/nome+tipo) e retorna o snapshot mais recente do grupo
// com count + last_used + lista de contrato_ids onde aparece.
export interface ISocioUnico {
  id: number;
  tipo_pessoa: TipoPessoaSocio;
  nome: string;
  cpf?: string | null;
  cnpj?: string | null;
  email?: string | null;
  telefone?: string | null;
  endereco?: string | null;
  profissao?: string | null;
  estado_civil?: string | null;
  representante_nome?: string | null;
  representante_cpf?: string | null;
  contratos_count: number;
  last_used?: string | null;
}

export interface ISociosUnicosFilters {
  page?: number;
  size?: number;
  tipo_pessoa?: TipoPessoaSocio;
  search?: string;
}

export interface ISociosUnicosPage {
  items: ISocioUnico[];
  total: number;
  page: number;
  size: number;
  pages: number;
  next_page: number | null;
  previous_page: number | null;
}

// ── Hook ──

export const useSocietario = () => {
  const queryClient = useQueryClient();

  /**
   * Sessao 2026-05-14 (review Matias PR #345 #5): invalidacao centralizada
   * apos mutations de contrato. Antes cada chamador reinventava a chamada
   * e esquecia chaves (ex: `regerarContrato` em `contratos/index.tsx` so
   * invalidava `['societario-contratos']`, deixando `['contrato', id]` e o
   * preview stale ate o WS chegar). Centralizado nas mutations garante
   * consistencia mesmo quando o WS estiver offline.
   *
   * Listagens invalidadas: `societario-contratos`, `*-preview`, `*-empresa`.
   * Detalhe individual: `['contrato', contratoId]` quando informado.
   */
  const invalidateContratoQueries = (contratoId?: number | null): void => {
    // Pega também a contagem das abas e o "Criado por" (chaves debaixo desta).
    queryClient.invalidateQueries({ queryKey: ['societario-contratos'] });
    queryClient.invalidateQueries({ queryKey: ['societario-contratos-preview'] });
    queryClient.invalidateQueries({ queryKey: ['societario-contratos-empresa'] });
    if (contratoId != null) {
      queryClient.invalidateQueries({ queryKey: ['contrato', contratoId] });
    }
  };

  /**
   * Mutations de socios invalidam tambem a aba "Cadastros / Sócios" (lista
   * distinct cross-contratos derivada de `tb_contrato_socio`).
   */
  const invalidateSociosQueries = (): void => {
    queryClient.invalidateQueries({ queryKey: ['societario-socios-unicos'] });
  };

  // Contratos
  const getContratos = async (key: QueryFunctionContext): Promise<IData<IContratoSocietario[]>> => {
    const filters = (key.queryKey?.[1] ?? {}) as IContratosFilters;
    const res = await api.get<IData<IContratoSocietario[]>>('/api/v1/societario/contratos', {
      params: filters,
    });
    return res.data;
  };

  const getContagemContratos = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IContagemContratos>> => {
    // Chave: ['societario-contratos', 'contagem', filtros].
    const filters = (key.queryKey?.[2] ?? {}) as IContagemContratosFilters;
    const res = await api.get<IDataNoPagination<IContagemContratos>>(
      '/api/v1/societario/contratos/contagem',
      { params: filters },
    );
    return res.data;
  };

  const getCriadoresContratos = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<ICriadorDeContratos[]>> => {
    // Chave: ['societario-contratos', 'criadores', filtros].
    const filters = (key.queryKey?.[2] ?? {}) as IContagemContratosFilters;
    const res = await api.get<IDataNoPagination<ICriadorDeContratos[]>>(
      '/api/v1/societario/contratos/criadores',
      { params: filters },
    );
    return res.data;
  };

  const getContrato = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IContratoSocietario>> => {
    const contratoId = key.queryKey?.[1];
    const res = await api.get<IDataNoPagination<IContratoSocietario>>(
      `/api/v1/societario/contratos/${contratoId}`,
    );
    return res.data;
  };

  const createContrato = async (
    data: IContratoSocietarioCreate,
  ): Promise<IDataNoPagination<IContratoSocietario>> => {
    const res = await api.post<IDataNoPagination<IContratoSocietario>>(
      '/api/v1/societario/contratos',
      data,
    );
    invalidateContratoQueries(res.data?.data?.id ?? null);
    return res.data;
  };

  const updateContrato = async ({
    id,
    data,
  }: {
    id: number;
    data: IContratoSocietarioUpdate;
  }): Promise<IDataNoPagination<IContratoSocietario>> => {
    const res = await api.patch<IDataNoPagination<IContratoSocietario>>(
      `/api/v1/societario/contratos/${id}`,
      data,
    );
    invalidateContratoQueries(id);
    return res.data;
  };

  const deleteContrato = async (id: number) => {
    const res = await api.delete(`/api/v1/societario/contratos/${id}`);
    invalidateContratoQueries(id);
    return res.data;
  };

  // Sessao 2026-05-08 (Fase D): arquiva sem apagar — status vira 'arquivado'.
  const arquivarContrato = async (id: number) => {
    const res = await api.post(`/api/v1/societario/contratos/${id}/arquivar`);
    invalidateContratoQueries(id);
    return res.data;
  };

  // Sócios
  const addSocio = async ({
    contratoId,
    data,
  }: {
    contratoId: number;
    data: IContratoSocioCreate;
  }): Promise<IDataNoPagination<IContratoSocio>> => {
    const res = await api.post<IDataNoPagination<IContratoSocio>>(
      `/api/v1/societario/contratos/${contratoId}/socios`,
      data,
    );
    invalidateContratoQueries(contratoId);
    invalidateSociosQueries();
    return res.data;
  };

  const updateSocio = async ({
    contratoId,
    socioId,
    data,
  }: {
    contratoId: number;
    socioId: number;
    data: IContratoSocioUpdate;
  }): Promise<IDataNoPagination<IContratoSocio>> => {
    const res = await api.patch<IDataNoPagination<IContratoSocio>>(
      `/api/v1/societario/contratos/${contratoId}/socios/${socioId}`,
      data,
    );
    invalidateContratoQueries(contratoId);
    invalidateSociosQueries();
    return res.data;
  };

  const deleteSocio = async ({ contratoId, socioId }: { contratoId: number; socioId: number }) => {
    const res = await api.delete(`/api/v1/societario/contratos/${contratoId}/socios/${socioId}`);
    invalidateContratoQueries(contratoId);
    invalidateSociosQueries();
    return res.data;
  };

  // Socios unicos (cross-contratos) — base reutilizavel da aba Socios/Cadastros.
  const getSociosUnicos = async (key: QueryFunctionContext): Promise<ISociosUnicosPage> => {
    const filters = (key.queryKey?.[1] ?? {}) as ISociosUnicosFilters;
    const res = await api.get<IDataNoPagination<ISociosUnicosPage>>('/api/v1/societario/socios', {
      params: filters,
    });
    return res.data.data;
  };

  // Geração / finalização / download
  const gerarContrato = async ({
    id,
    body,
    engine,
  }: {
    id: number;
    /** Body completo do request — Fase 4 ganhou enquadramento_empresarial,
     *  tipos_alteracao e consolidar. Default vazio mantem retro-compat. */
    body?: IGerarContratoRequest;
    /** Engine override — obrigatorio na pratica (caller passa v3 ou v2). */
    /**
     * Engine de geracao — só `'v3'` desde a aposentadoria do v1 e do v2
     * (backend #1299/#1302; a rota valida com `regex="^v3$"`).
     * - constituicao: docxtpl PF/PJ uni/pluri (refinado pelo backend).
     * - alteracao: DIFF+APPLY contra vigente parseado (Bloco 13 D+E) —
     *   exige `body.patches[]` + `body.vigente_hash`. Sync.
     */
    engine?: 'v3';
  }): Promise<IDataNoPagination<IGerarContratoResultado>> => {
    const res = await api.post<IDataNoPagination<IGerarContratoResultado>>(
      `/api/v1/societario/contratos/${id}/gerar`,
      body ?? {},
      // ⚠️ timeout PRÓPRIO: o padrão do axios é 120s, e a PRIMEIRA geração de um
      // contrato paga a leitura do vigente por IA (cache frio) mais o refino —
      // medido em 2min16s num contrato real, ou seja, 16s ALÉM do padrão. O
      // request era cancelado no navegador enquanto o backend seguia gerando e
      // gravando: o operador via erro, clicava de novo e pagava a IA duas vezes.
      // Da segunda vez em diante o cache está quente e leva ~11s.
      { params: { engine }, timeout: 300000 },
    );
    // Sessao 2026-05-14 (review Matias PR #345 #5): em v2 o sync retorna
    // `processing` e o terminal vem pelo WebSocket — mas invalidamos aqui
    // tambem pra refletir mudancas imediatas no contrato (task_id_em_andamento
    // setado, decisoes_ia/campos_pendentes em respostas sincronas v1).
    invalidateContratoQueries(id);
    return res.data;
  };

  const getStatusGeracaoV2 = async (contratoId: number): Promise<IStatusGeracaoV2> => {
    const res = await api.get<IDataNoPagination<IStatusGeracaoV2>>(
      `/api/v1/societario/contratos/${contratoId}/gerar/status`,
    );
    return res.data.data;
  };

  // Fase 5 — Validators v2.
  const validarContrato = async (contratoId: number): Promise<IIssue[]> => {
    const res = await api.post<IDataNoPagination<IIssue[]>>(
      `/api/v1/societario/contratos/${contratoId}/validar`,
    );
    return res.data.data;
  };

  // Sessao 2026-05-11 (item 26 Fase 5): preview deterministico — esqueleto
  // textual sem IA, util pra operador conferir estrutura antes de gastar
  // tokens. Backend: app/services/societario_v2/preview_deterministico.py
  const previewDeterministico = async (
    contratoId: number,
    // Alteração: corpo com patches/vigente_hash/quadro_vigente_override (mesmos
    // inputs do /gerar) — sem ele, o back devolve "monte ao menos uma alteração".
    // Constituição/transformação renderizam do próprio contrato (sem corpo).
    body?: Record<string, unknown>,
  ): Promise<{
    texto: string;
    engine: string;
    tem_placeholders?: boolean;
    conferir?: string[];
  }> => {
    const res = await api.post<
      IDataNoPagination<{
        texto: string;
        engine: string;
        tem_placeholders?: boolean;
        conferir?: string[];
      }>
    >(`/api/v1/societario/contratos/${contratoId}/preview-deterministico`, body ?? undefined);
    return res.data.data;
  };

  // Fase 5.5 — Dashboard de metricas.
  const getMetrics = async (dias: number = 30): Promise<IMetricasResponse> => {
    const res = await api.get<IDataNoPagination<IMetricasResponse>>(
      '/api/v1/societario/admin/metrics',
      { params: { dias } },
    );
    return res.data.data;
  };

  /**
   * Subscribe ao status da geracao v2 com **WebSocket + polling em paralelo**
   * (primeiro a chegar vence) e dedup via flag de estado terminal.
   *
   * Retorna funcao de cleanup. Chame no unmount/cancelamento.
   *
   * @example
   * useEffect(() => {
   *   const off = subscribeGeracaoStatus({
   *     contratoId: 42,
   *     onUpdate: s => console.log('processing', s),
   *     onTerminal: t => {
   *       toast.success(`Geracao ${t.status} (via ${t.source})`);
   *       navigate(`/contratos/${contratoId}/revisao`);
   *     },
   *   });
   *   return off;
   * }, [contratoId]);
   */
  const subscribeGeracaoStatus = (opts: {
    contratoId: number;
    /** Callback em cada poll OK (para timer/UI). Nao chamado no terminal. */
    onUpdate?: (status: IStatusGeracaoV2) => void;
    /** Disparado UMA vez quando a geracao chega em estado terminal. */
    onTerminal: (final: {
      status: 'gerado' | 'em_revisao' | 'campos_pendentes' | 'falha_geracao' | 'no_task';
      source: 'websocket' | 'polling';
      data?: any;
    }) => void;
    /** Erros do polling (rede etc). Polling continua tentando. */
    onError?: (err: Error) => void;
    /** Backoff inicial do polling, ms. Default 2000. */
    pollInitialMs?: number;
    /** Backoff maximo, ms. Default 10000. */
    pollMaxMs?: number;
  }): (() => void) => {
    const initialMs = opts.pollInitialMs ?? 2000;
    const maxMs = opts.pollMaxMs ?? 10000;

    let terminal = false;
    let pollAbort = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanupAll = () => {
      pollAbort = true;
      if (timeoutId) clearTimeout(timeoutId);
      offWs();
    };

    // Dedup: primeira fonte (WS ou polling) que chegar em terminal vence.
    // Subsequentes sao no-op — evita toast/redirect duplo.
    const complete = (final: {
      status: 'gerado' | 'em_revisao' | 'campos_pendentes' | 'falha_geracao' | 'no_task';
      source: 'websocket' | 'polling';
      data?: any;
    }) => {
      if (terminal) return;
      terminal = true;
      cleanupAll();
      opts.onTerminal(final);
    };

    // ── 1. WebSocket subscription ──
    const offWs = onSocietarioGerado(detail => {
      if (detail.contratoId !== opts.contratoId) return;
      const { status } = detail;
      if (status === 'gerado' || status === 'em_revisao' || status === 'campos_pendentes') {
        complete({ status, source: 'websocket', data: detail });
      } else if (status === 'falha_geracao') {
        complete({ status: 'falha_geracao', source: 'websocket', data: detail });
      }
    });

    // ── 2. Polling com backoff exponencial em paralelo ──
    let delay = initialMs;
    const pollOnce = async () => {
      if (pollAbort) return;
      try {
        const status = await getStatusGeracaoV2(opts.contratoId);
        if (pollAbort) return;
        if (status.status === 'completed') {
          const finalStatus =
            (status.result?.status as 'gerado' | 'em_revisao' | 'campos_pendentes') ?? 'gerado';
          complete({ status: finalStatus, source: 'polling', data: status.result });
          return;
        }
        if (status.status === 'failed') {
          complete({ status: 'falha_geracao', source: 'polling', data: status });
          return;
        }
        if (status.status === 'no_task' || status.status === 'not_found') {
          // Sessao 2026-05-14 (review Matias PR #345 #4): `not_found` aqui
          // representava a mesma situacao de `no_task` (chave Redis expirada
          // pelo TTL de 1h com `task_id_em_andamento` ainda setado no
          // contrato), mas caia no fallback final e o setTimeout reagendava
          // outro poll pra sempre — loop infinito em qualquer aba aberta.
          // Trata os dois iguais: busca o contrato pra ver status final.
          // Soh marcamos falha_geracao se o contrato esta efetivamente em estado
          // de erro. rascunho/cancelado/arquivado/assinado sao estados validos
          // (pre-geracao, decisao do operador, pos-assinatura) e nao devem
          // disparar UX de falha — caem em no_task pra o caller decidir.
          try {
            const contratoRes = await api.get<IDataNoPagination<IContratoSocietario>>(
              `/api/v1/societario/contratos/${opts.contratoId}`,
            );
            const c = contratoRes.data.data;
            let finalStatus:
              | 'gerado'
              | 'em_revisao'
              | 'campos_pendentes'
              | 'falha_geracao'
              | 'no_task';
            if (c.status === 'gerado' || c.status === 'assinado') finalStatus = 'gerado';
            else if (c.status === 'em_revisao') finalStatus = 'em_revisao';
            else if (c.status === 'campos_pendentes') finalStatus = 'campos_pendentes';
            else finalStatus = 'no_task';
            complete({ status: finalStatus, source: 'polling', data: c });
          } catch {
            complete({ status: 'no_task', source: 'polling', data: status });
          }
          return;
        }
        // status === 'processing' — segue polling.
        opts.onUpdate?.(status);
      } catch (err) {
        opts.onError?.(err as Error);
        // Erro de rede nao para polling — proximo tick tenta de novo.
      }
      if (pollAbort) return;
      // Backoff exponencial ate o teto.
      delay = Math.min(delay * 1.5, maxMs);
      timeoutId = setTimeout(pollOnce, delay);
    };
    // Inicia primeiro tick imediato (t=0) — paralelo com o WS.
    timeoutId = setTimeout(pollOnce, 0);

    return cleanupAll;
  };

  const downloadContrato = async (
    id: number,
    opcoes?: { original?: boolean },
  ): Promise<IDataNoPagination<IDownloadContratoResponse>> => {
    // Sem `original`, o backend serve a versão revisada quando ela existe.
    // Com `original: true`, força o DOCX que o sistema gerou — é o que torna o
    // par (gerado, revisado) comparável.
    const res = await api.get<IDataNoPagination<IDownloadContratoResponse>>(
      `/api/v1/societario/contratos/${id}/download`,
      opcoes?.original ? { params: { original: true } } : undefined,
    );
    return res.data;
  };

  const uploadDocumentoEditado = async ({
    contratoId,
    file,
  }: {
    contratoId: number;
    file: File;
  }): Promise<IDataNoPagination<{ contrato_id: number; s3_key: string }>> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post<IDataNoPagination<{ contrato_id: number; s3_key: string }>>(
      `/api/v1/societario/contratos/${contratoId}/documento-editado`,
      formData,
    );
    return res.data;
  };

  // CNH / Textract
  const analyzeCnh = async ({
    file,
    contrato_id,
    socio_id,
  }: {
    file: File;
    contrato_id?: number;
    socio_id?: number;
  }): Promise<IDataNoPagination<ICnhAnalyzeResponse>> => {
    const formData = new FormData();
    formData.append('file', file);
    if (contrato_id !== undefined) formData.append('contrato_id', String(contrato_id));
    if (socio_id !== undefined) formData.append('socio_id', String(socio_id));
    const res = await api.post<IDataNoPagination<ICnhAnalyzeResponse>>(
      '/api/v1/societario/cnh/analyze',
      formData,
    );
    return res.data;
  };

  // Documento de identidade generico (substitui analyzeCnh em fluxos novos —
  // aceita CNH+RG, PDF+JPG+PNG, tipo auto-detectavel). Endpoint devolve 422
  // quando extracao e vazia (nome=null OU cpf+rg ambos null) — frontend
  // mostra erro claro pra operador re-tentar.
  const analyzeDocumento = async ({
    file,
    tipo = 'auto',
    contrato_id,
    socio_id,
  }: {
    file: File;
    tipo?: TipoDocumentoRequest;
    contrato_id?: number;
    socio_id?: number;
  }): Promise<IDataNoPagination<IDocumentoAnalyzeResponse>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tipo', tipo);
    if (contrato_id !== undefined) formData.append('contrato_id', String(contrato_id));
    if (socio_id !== undefined) formData.append('socio_id', String(socio_id));
    const res = await api.post<IDataNoPagination<IDocumentoAnalyzeResponse>>(
      '/api/v1/societario/documento/analyze',
      formData,
    );
    return res.data;
  };

  // Consulta CNPJ pra autopreencher sócio PJ (sede + representante + junta_uf).
  // CPF do representante vem null nas bases públicas (mascarado) — operador digita.
  const consultarCnpj = async (cnpj: string): Promise<ICnpjConsultaResponse> => {
    const cnpjLimpo = (cnpj || '').replace(/\D/g, '');
    const res = await api.get<IDataNoPagination<ICnpjConsultaResponse>>(
      '/api/v1/societario/cnpj/consultar',
      { params: { cnpj: cnpjLimpo } },
    );
    return res.data.data;
  };

  // CNAE
  const buscarCnaes = async (q: string, limit = 20): Promise<ICnae[]> => {
    const res = await api.get<IDataNoPagination<ICnae[]>>('/api/v1/societario/cnaes/buscar', {
      params: { q, limit },
    });
    return res.data.data;
  };

  const sugerirCnaes = async (descricao_atividade: string): Promise<ISugerirCnaesResponse> => {
    const res = await api.post<IDataNoPagination<ISugerirCnaesResponse>>(
      '/api/v1/societario/cnaes/sugerir',
      { descricao_atividade },
    );
    return res.data.data;
  };

  // Modelos de referencia (markdown editavel — Fase 1)
  const getModeloAtivo = async (
    tipo_operacao: TipoOperacaoSocietario,
    empresa_id?: number | null,
  ): Promise<IModeloReferencia | null> => {
    const res = await api.get<IDataNoPagination<IModeloReferencia | null>>(
      '/api/v1/societario/modelos/ativo',
      { params: { tipo_operacao, empresa_id } },
    );
    return res.data.data;
  };

  const criarModeloReferencia = async (data: ICriarModeloRequest): Promise<IModeloReferencia> => {
    const res = await api.post<IDataNoPagination<IModeloReferencia>>(
      '/api/v1/societario/modelos',
      data,
    );
    return res.data.data;
  };

  // Exemplos por contrato (Fase 2 — IA-first com PII redaction)
  const uploadExemplo = async ({
    contratoId,
    nome,
    file,
  }: {
    contratoId: number;
    nome: string;
    file: File;
  }): Promise<IUploadExemploResponse> => {
    const formData = new FormData();
    formData.append('nome', nome);
    formData.append('file', file);
    const res = await api.post<IDataNoPagination<IUploadExemploResponse>>(
      `/api/v1/societario/contratos/${contratoId}/exemplos`,
      formData,
    );
    return res.data.data;
  };

  const listarExemplos = async (contratoId: number): Promise<IContratoExemplo[]> => {
    const res = await api.get<IDataNoPagination<IContratoExemplo[]>>(
      `/api/v1/societario/contratos/${contratoId}/exemplos`,
    );
    return res.data.data;
  };

  const removerExemplo = async ({
    contratoId,
    exemploId,
  }: {
    contratoId: number;
    exemploId: number;
  }) => {
    const res = await api.delete(
      `/api/v1/societario/contratos/${contratoId}/exemplos/${exemploId}`,
    );
    return res.data;
  };

  // Contrato vigente (Fase 2.5 — obrigatorio em alteracoes)
  const uploadContratoVigente = async ({
    contratoId,
    file,
  }: {
    contratoId: number;
    file: File;
  }): Promise<IContratoVigente> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post<IDataNoPagination<IContratoVigente>>(
      `/api/v1/societario/contratos/${contratoId}/contrato-vigente`,
      formData,
    );
    return res.data.data;
  };

  const getContratoVigente = async (contratoId: number): Promise<IContratoVigente | null> => {
    const res = await api.get<IDataNoPagination<IContratoVigente | null>>(
      `/api/v1/societario/contratos/${contratoId}/contrato-vigente`,
    );
    return res.data.data;
  };

  /**
   * Presigned URL (attachment + nome amigavel) pro DOWNLOAD do vigente
   * anexado. Diferente do `getContratoVigente` (presigned inline pro preview),
   * este forca o navegador a SALVAR com nome legivel. Sem auditor-gate.
   */
  const downloadContratoVigente = async (
    id: number,
  ): Promise<IDataNoPagination<IDownloadContratoResponse>> => {
    const res = await api.get<IDataNoPagination<IDownloadContratoResponse>>(
      `/api/v1/societario/contratos/${id}/contrato-vigente/download`,
    );
    return res.data;
  };

  /**
   * Bloco 13 v3 alteração — parse lazy do vigente + hash-lock G-5.
   * Wizard chama ao abrir o passo de alteração (depois de upload do
   * vigente) pra:
   *  - exibir o vigente parseado (cláusulas + quadro de sócios) ao
   *    operador antes de ele montar os patches;
   *  - capturar `vigente_hash` (SHA1[:12]) e enviar de volta no /gerar.
   *
   * Strict ativo (A.39): /gerar rejeita request sem hash em prod.
   * Divergência entre hash montagem e hash atual → 412 com
   * `detail.codigo='VIGENTE_HASH_MISMATCH'` — UI mostra modal
   * "vigente foi recarregado, refaça os patches".
   */
  // Chat da administração da alteração (29/09).
  // Resumo e mensagem respondem na hora com `pendente` (a IA roda em segundo plano: o ALB corta em
  // 60 s) e a tela acompanha por este GET até o pedido sair de `pendente`.
  const estadoDaAdministracao = async (
    contratoId: number,
    assunto: AssuntoDoChat = 'administracao',
  ): Promise<IAdministracaoIA> => {
    const res = await api.get<IDataNoPagination<IAdministracaoIA>>(
      `/api/v1/societario/contratos/${contratoId}/${assunto}`,
    );
    return res.data.data;
  };

  const resumoDaAdministracao = async (
    contratoId: number,
    assunto: AssuntoDoChat = 'administracao',
  ): Promise<IAdministracaoIA> => {
    const res = await api.post<IDataNoPagination<IAdministracaoIA>>(
      `/api/v1/societario/contratos/${contratoId}/${assunto}/resumo`,
    );
    return res.data.data;
  };

  const mensagemDaAdministracao = async (
    contratoId: number,
    corpo: {
      mensagem: string;
      pessoas: IPessoaDaAdministracao[];
      /** O texto na tela (com o que o operador editou): a IA ajusta a partir dele. */
      rascunho?: IRascunhoDaAdministracao | null;
    },
    assunto: AssuntoDoChat = 'administracao',
  ): Promise<IAdministracaoIA> => {
    const res = await api.post<IDataNoPagination<IAdministracaoIA>>(
      `/api/v1/societario/contratos/${contratoId}/${assunto}/mensagem`,
      corpo,
    );
    return res.data.data;
  };

  const aprovarAdministracao = async (
    contratoId: number,
    rascunho: IRascunhoDaAdministracao,
    pessoas: IPessoaDaAdministracao[] = [],
    /** O vigente da redação na tela: outro arquivo agora, o back recusa (numeração de outro documento). */
    vigente: string | null = null,
    assunto: AssuntoDoChat = 'administracao',
  ): Promise<IAdministracaoIA> => {
    const res = await api.post<IDataNoPagination<IAdministracaoIA>>(
      `/api/v1/societario/contratos/${contratoId}/${assunto}/aprovar`,
      { rascunho, pessoas, vigente },
    );
    return res.data.data;
  };

  const recomecarAdministracao = async (
    contratoId: number,
    assunto: AssuntoDoChat = 'administracao',
  ): Promise<void> => {
    await api.delete(`/api/v1/societario/contratos/${contratoId}/${assunto}`);
  };

  // Textos prontos (15/10), com a concordância dos sócios deste contrato.
  const textosProntos = async (contratoId: number): Promise<ITextoPronto[]> => {
    const res = await api.get<IDataNoPagination<ITextoPronto[]>>(
      `/api/v1/societario/contratos/${contratoId}/clausulas/textos-prontos`,
    );
    return res.data.data;
  };

  const parsearVigentePreview = async (
    contratoId: number,
  ): Promise<IParsearVigentePreviewResponse> => {
    const res = await api.get<IDataNoPagination<IParsearVigentePreviewResponse>>(
      `/api/v1/societario/contratos/${contratoId}/parsear-vigente-preview`,
    );
    return res.data.data;
  };

  /**
   * Pos-Fase 6: cria contrato em `rascunho_inicial` ja com upload do contrato
   * vigente + extracao IA. Usado pelo wizard de alteracao no step inicial,
   * permitindo pre-preenchimento dos campos do form a partir do PDF/DOCX.
   *
   * Quando o usuario avanca do step contrato_vigente, o frontend faz PATCH
   * com `advance_from_initial: true` pra transicionar pra `rascunho`.
   *
   * Resilient: se a IA falhar, `dados_extraidos=null` mas o contrato e
   * criado mesmo assim — frontend mostra "preencha manualmente".
   */
  const iniciarAlteracaoComVigente = async ({
    file,
  }: {
    file: File;
  }): Promise<IIniciarAlteracaoResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post<IDataNoPagination<IIniciarAlteracaoResponse>>(
      '/api/v1/societario/contratos/iniciar-alteracao-com-vigente',
      formData,
    );
    // Sessao 2026-05-15 (review final P0 A2): cria contrato em
    // `rascunho_inicial` no backend — invalidar listagens pra que o stub
    // apareca imediatamente e operador nao re-upload pensando que falhou
    // (gerando contratos orfaos).
    invalidateContratoQueries(res.data?.data?.contrato_id ?? null);
    return res.data.data;
  };

  // Templates (legacy DOCX — mantido para compatibilidade pre-Fase 1)
  const getTemplates = async (key: QueryFunctionContext): Promise<IData<ITemplateSocietario[]>> => {
    const filters = (key.queryKey?.[1] ?? {}) as ITemplatesFilters;
    const res = await api.get<IData<ITemplateSocietario[]>>('/api/v1/societario/templates', {
      params: filters,
    });
    return res.data;
  };

  const createTemplate = async (
    data: ITemplateSocietarioCreateForm,
  ): Promise<IDataNoPagination<ITemplateSocietario>> => {
    const formData = new FormData();
    formData.append('nome', data.nome);
    formData.append('tipo_operacao', data.tipo_operacao);
    formData.append('versao', data.versao);
    if (data.descricao) formData.append('descricao', data.descricao);
    formData.append('ativo', String(data.ativo ?? true));
    formData.append('file', data.file);
    const res = await api.post<IDataNoPagination<ITemplateSocietario>>(
      '/api/v1/societario/templates',
      formData,
    );
    return res.data;
  };

  const updateTemplate = async ({
    id,
    data,
  }: {
    id: number;
    data: ITemplateSocietarioUpdate;
  }): Promise<IDataNoPagination<ITemplateSocietario>> => {
    const res = await api.patch<IDataNoPagination<ITemplateSocietario>>(
      `/api/v1/societario/templates/${id}`,
      data,
    );
    return res.data;
  };

  const deleteTemplate = async (id: number) => {
    const res = await api.delete(`/api/v1/societario/templates/${id}`);
    return res.data;
  };

  return {
    getContratos,
    getContagemContratos,
    getCriadoresContratos,
    getContrato,
    createContrato,
    updateContrato,
    deleteContrato,
    arquivarContrato,
    addSocio,
    updateSocio,
    deleteSocio,
    getSociosUnicos,
    gerarContrato,
    getStatusGeracaoV2,
    subscribeGeracaoStatus,
    validarContrato,
    previewDeterministico,
    getMetrics,
    downloadContrato,
    uploadDocumentoEditado,
    analyzeCnh,
    analyzeDocumento,
    consultarCnpj,
    buscarCnaes,
    sugerirCnaes,
    getModeloAtivo,
    criarModeloReferencia,
    uploadExemplo,
    listarExemplos,
    removerExemplo,
    uploadContratoVigente,
    getContratoVigente,
    downloadContratoVigente,
    parsearVigentePreview,
    estadoDaAdministracao,
    resumoDaAdministracao,
    mensagemDaAdministracao,
    aprovarAdministracao,
    recomecarAdministracao,
    textosProntos,
    iniciarAlteracaoComVigente,
    getTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  };
};
